import { ChangeEvent, RefObject } from 'react';
import { DropDownItemValueProps } from 'spindl-shared-components';
import { Entry, EntryValueType } from '../modules/entries/types';
import { FieldSchema, FieldTypes } from '../modules/tables/types';
import {
  BASE_URL,
  DATA_PATH,
  DATA_URL,
  IS_DEV,
  SERVICE_URL_TEMPLATE,
} from './config';
import { UNDERLINE_COLOURS, UNFILTERABLE_FIELD_TYPES, UNSORTABLE_FIELD_TYPES } from './constants';
import { BooleanOption, BooleanOptions, DropDownOption, KeyDownOptions, UnderlineStyleEnum } from './types';

export const isEmail = (value: string) => {
  // eslint-disable-next-line
  const regexEmail = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regexEmail.test(value);
};

export const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const isEmptyString = (value?: string) =>
  value ? value.trim().length === 0 : true;

export const getQueryParams = (url: string): Record<string, string> => {
  const results: Record<string, string> = {};
  const queryParams = url.split('?');
  if (queryParams.length > 1) {
    const params = queryParams[1].split('&');

    for (let i = 0, len = params.length; i < len; i += 1) {
      const parts = params[i].split('=');
      if (parts.length > 1) {
        const [key, value] = parts;
        results[key] = value;
      }
    }
  }
  return results;
};
  

export const mapQueryString = (payload: Record<string, unknown>): string =>
  Object.entries(payload)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

export const addQueryParam = (
  url: string,
  payload: Record<string, string>,
): string => {
  let queryParams = getQueryParams(url);
  queryParams = {
    ...queryParams,
    ...payload,
  };
  const queryString = mapQueryString(queryParams);
  const baseUrl = url.split('?')[0];
  return `${baseUrl}?${queryString}`;
};

export const getUnderline = (index: number): UnderlineStyleEnum => {
  return UNDERLINE_COLOURS[index % UNDERLINE_COLOURS.length];
};

export const isKeyDown = (
  event: React.KeyboardEvent<HTMLTextAreaElement>,
  options: KeyDownOptions,
) => {
  return (
    event.key === options.key &&
    ((event.altKey && options.alt) ||
      (event.ctrlKey && options.ctrl) ||
      (event.shiftKey && options.shift))
  );
};

const extractUrlDetails = (
  url: string,
): {
  appId?: string;
  serviceName?: string;
} => {
  const serviceUrlRegexTemplate = (SERVICE_URL_TEMPLATE as string)
    .replace('{appId}', '([a-zA-Z0-9]*)')
    .replace('{serviceName}', '([a-zA-Z0-9]*)');

  const serviceUrlRegex = new RegExp(serviceUrlRegexTemplate);
  const results = serviceUrlRegex.exec(url);

  if (results) {
    const appId = results[1];
    const serviceName = results[2];
    return {
      appId,
      serviceName,
    };
  }
  return {};
};

export const getServerUrl = (servicePath?: string): string => {
  if (IS_DEV) {
    return DATA_URL;
  }

  const { appId } = extractUrlDetails(window.location.pathname);
  const service = servicePath || DATA_PATH;
  return `${BASE_URL}/${appId}/${service}`;
};

// Cell and field utils
export const isValidTextValue = (cellValue?: EntryValueType) => {
  return (typeof cellValue === 'string' || typeof cellValue === 'number');
};

export const isFilterable = (fieldType?: FieldTypes) => fieldType && !UNFILTERABLE_FIELD_TYPES.includes(fieldType);

export const isSortable = (fieldType?: FieldTypes) => fieldType && !UNSORTABLE_FIELD_TYPES.includes(fieldType);

export const isContentAnEntry = (value?: EntryValueType): value is Entry => {
  return (value as Entry)?.uuid !== undefined;
};

export const isContentAnArrayOfEntries = (value?: EntryValueType): value is Entry[] => {
  return Array.isArray(value) && [...value].every((item) => isContentAnEntry(item));
};

export const entryToUuidForm = (value: EntryValueType): EntryValueType => {
  if (isContentAnEntry(value)) {
    return value.uuid;
  } else if (isContentAnArrayOfEntries(value)) {
    return value.map((x) => x.uuid);
  }
  return value;
};

// Text fields utils
export const handleTextFieldFirstSelect = (
  textAreaRef: React.RefObject<HTMLTextAreaElement>,
) => {
  const textArea = textAreaRef.current;
  textArea?.focus();
};

export const handleTextFieldSecondSelect = (
  textAreaRef: React.RefObject<HTMLTextAreaElement>,
) => {
  const textArea = textAreaRef.current;
  const endContent = textArea?.value.length || 0;
  textArea?.setSelectionRange(endContent, endContent); // set caret position to end of content
  textArea?.focus();
};

export const shouldTextFieldExpand = (
  textAreaRef: React.RefObject<HTMLTextAreaElement>,
  initialTextAreaHeight: number,
) => {
  const currentTextAreaHeight = textAreaRef.current?.clientHeight || 0;
  return currentTextAreaHeight > initialTextAreaHeight;
};

export const CHANGE_TEXT_EXCEPTION_KEYS: string[] = [
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Shift',
  'Meta',
  'Control',
  'Alt',
];

// Number field utils
const NUMBER_AND_FIRST_HYPHEN_REGEX = /[^0-9.-]|(?!^)-/g;
const ALL_DOTS_REGEX = /^([^.]*\.)(.*)/;
const LAST_DOT_REGEX = /\./g;

export const applyNumberMask = (
  ref: RefObject<HTMLTextAreaElement>,
  event: ChangeEvent<HTMLTextAreaElement>,
) => {
  if (ref.current) {
    ref.current.value = event.target.value
      .replace(NUMBER_AND_FIRST_HYPHEN_REGEX, '')
      .replace(ALL_DOTS_REGEX, (_, g1: string, g2: string) => {
        return g1 + g2.replace(LAST_DOT_REGEX, '');
      });
  }
};

// Date fields utils
// For formatting in the datepicker - uses date-fns
export const TIME_ONLY_DATEFNS_FORMAT = "h:mm aa 'ET'";
export const DATE_ONLY_DATEFNS_FORMAT = 'MMMM d, yyyy';
export const DATE_TIME_DATEFNS_FORMAT = `${DATE_ONLY_DATEFNS_FORMAT} ${TIME_ONLY_DATEFNS_FORMAT}`;

const DATEFNS_FORMAT_MAP = {
  date: DATE_ONLY_DATEFNS_FORMAT,
  'date-time': DATE_TIME_DATEFNS_FORMAT,
  time: TIME_ONLY_DATEFNS_FORMAT,
};

export const getDateFnsFormat = (dataType: FieldTypes) => {
  return DATEFNS_FORMAT_MAP[dataType];
};

// For formatting the date in the default cell state
const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

const HOUR_FORMAT: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric',
};

// Formats date to following format: March 10, 2022 12:00AM ET
export const formatDatepickerDate = (
  date: Date,
  showTime: boolean,
  timeOnly = false,
) => {
  let newDate = '';
  if (!timeOnly) {
    newDate = date?.toLocaleDateString('en-US', DATE_FORMAT);
  }
  if (showTime || timeOnly) {
    newDate = newDate.concat(
      ' ',
      date?.toLocaleTimeString('en-US', HOUR_FORMAT),
      ' ET',
    );
  }

  return newDate;
};

export const checkPreventCalendarBlur = (
  event: React.FocusEvent<HTMLDivElement | HTMLTextAreaElement>,
) => {
  const relatedTarget = event.relatedTarget as HTMLDivElement;
  const target = event.target as HTMLDivElement;
  return (
    relatedTarget &&
    (relatedTarget.parentElement?.className.includes('react-datepicker') ||
      relatedTarget.parentElement?.className.includes('CellCalendarModal') ||
      target?.className.includes('react-datepicker'))
  );
};

// JSON utils
export const copyAndFormatJSON = async (content: string) => {
  let target;
  try {
    target = JSON.parse(content);
  } catch (SyntaxError) {
    target = content;
  }
  await navigator.clipboard.writeText(JSON.stringify(target, null, '\t'));
};

// Boolean field utils
const optionTextToBoolean = (optionText: BooleanOption): boolean => {
  return optionText === BooleanOptions.True;
};

export const booleanToOptionText = (option: boolean): BooleanOptions => {
  return option ? BooleanOptions.True : BooleanOptions.False;
};

// Select fields utils
const parseReference = (entry: Entry, fieldNames?: string[]): string => {
  const option = fieldNames?.length ? fieldNames.map((name) => {
    return entry.content[name];
  }).join(' ') : entry.uuid;
  return option;
};

export const resolveReference = (
  entryContent: EntryValueType,
  fieldType?: FieldTypes,
  feildSchema?: FieldSchema,
): EntryValueType | undefined => {
  const fieldNames = feildSchema?.reference?.fieldNames;
  if (fieldType === 'single-reference') {
    const fieldValue = entryContent as Entry;
    return fieldValue && parseReference(fieldValue, fieldNames);
  } else if (fieldType === 'multi-reference') {
    const fieldValue = entryContent as Entry[];
    const options: string[] = [];
    fieldValue.forEach((item) => {
      const option = parseReference(item, fieldNames);
      if (option) {
        options.push(option);
      }
    });
    return options;
  }
};

export const getSelectedOption = (
  entryContent: EntryValueType,
  fieldType?: FieldTypes,
  feildSchema?: FieldSchema,
): EntryValueType | undefined => {
  if (fieldType === 'select') {
    return entryContent ? String(entryContent) : undefined;
  } else if ((fieldType === 'single-reference' || fieldType === 'multi-reference')) {
    return resolveReference(entryContent, fieldType, feildSchema);
  } else if (fieldType === 'boolean' && typeof entryContent === 'boolean') {
    return booleanToOptionText(entryContent);
  }
};

export const getSelectFieldOptionProps = (
  option: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: any,
): DropDownItemValueProps => {
  return {
    text: {
      value: option,
    },
    metadata: {
      text: option,
      value: metadata,
    },
  };
};

export const getReferenceOptionProps = (
  entryContent?: EntryValueType,
  fieldType?: FieldTypes,
  feildSchema?: FieldSchema,
): DropDownItemValueProps[] => {
  const referenceFieldOptions: [string, Entry][] = [];
  if (entryContent) {
    const fieldNames = feildSchema?.reference?.fieldNames;
    if (fieldType === 'single-reference' && !Array.isArray(entryContent)) {
      const fieldValue = entryContent as Entry;
      const option = parseReference(fieldValue, fieldNames);
      if (option) {
        referenceFieldOptions.push([option, fieldValue]);
      }
    } else if (fieldType === 'multi-reference' || fieldType === 'single-reference') {
      const fieldValue = entryContent as Entry[];
      fieldValue.forEach((item) => {
        const option = parseReference(item, fieldNames);
        if (option) {
          referenceFieldOptions.push([option, item]);
        }
      });
    }
  }

  return referenceFieldOptions.map((referenceFieldOption) => {
    const [referenceableContent, entryUuid] = referenceFieldOption;
    return getSelectFieldOptionProps(referenceableContent, entryUuid);
  });
};

export const getSelectOptions = (
  entryContent?: EntryValueType,
  fieldType?: FieldTypes,
  feildSchema?: FieldSchema,
): DropDownItemValueProps[] | undefined => {
  if (fieldType === 'select') {
    return (feildSchema?.options || []).map(option => {
      return getSelectFieldOptionProps(option);
    });
  } else if (fieldType === 'single-reference' || fieldType === 'multi-reference') {
    if (entryContent) {
      return getReferenceOptionProps(entryContent, fieldType, feildSchema);
    }
  } else if (fieldType === 'boolean') {
    return [
      getSelectFieldOptionProps(BooleanOptions.True),
      getSelectFieldOptionProps(BooleanOptions.False),
    ];
  }
};

export const getOptions = (
  entryContent?: EntryValueType,
  fieldType?: FieldTypes,
  feildSchema?: FieldSchema,
  referenceOptions?: Entry[],
  isReference?: boolean,
): DropDownItemValueProps[] | undefined => {
  if (isReference) {
    return getSelectOptions(referenceOptions, fieldType, feildSchema);
  }
  return getSelectOptions(entryContent, fieldType, feildSchema);
};

export const optionToValue = (
  entryValue?: EntryValueType,
  fieldType?: FieldTypes,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: any,
): EntryValueType | undefined => {
  if (fieldType === 'select') {
    return entryValue;
  } else if (fieldType === 'single-reference') {
    const { value } = metadata as DropDownOption;
    return value;
  } else if (fieldType === 'multi-reference') {
    let optionsToSave: string[] = [];
    if (Array.isArray(entryValue)) {
      optionsToSave = [...entryValue, metadata];      
    } else {
      optionsToSave = [metadata];
    }
    return optionsToSave;
  } else if (fieldType === 'boolean') {
    return optionTextToBoolean(entryValue as BooleanOption); 
  }
};

// Data search utils
export const noResultsFound = (noOfRecords: number, totalPages: number, filtersApplied: boolean): boolean => {
  return noOfRecords === 0 && totalPages === 1 && filtersApplied;
};

export const debounce = (callback, delay = 500) => {
  let timeout: NodeJS.Timeout;

  return (...args) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};
